// src/api/files/files.ts
import { api } from "../api";
import type { FileResponse, FileListItem, PaginatedFilesResponse, ListFilesParams } from "./files.types";
import { trackEvent } from "../../utils/analytics";
import { isNative } from "../../platform";
import { ensureStoragePermission } from "../../platform/storage-permission";
import { emitPermissionAlert } from "../../contexts/permission-alert-context";
import { emitToast } from "../../contexts/toast-context";

/** Save a blob to device storage using Capacitor Filesystem. */
async function saveToDevice(blob: Blob, fileName: string): Promise<void> {
    const granted = await ensureStoragePermission();
    if (!granted) {
        emitPermissionAlert('storage');
        return;
    }

    const { Filesystem, Directory } = await import('@capacitor/filesystem');

    const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            const result = reader.result as string;
            const idx = result.indexOf(',');
            resolve(idx >= 0 ? result.slice(idx + 1) : result);
        };
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
    });

    await Filesystem.writeFile({
        path: fileName,
        data: base64,
        directory: Directory.Documents,
        recursive: true,
    });
}

export const filesApi = {
    async listFiles(): Promise<FileListItem[]> {
        const response = await api.get<FileResponse[]>("/files");
        const data = Array.isArray(response.data) ? response.data : [];
        
        // Transform backend response to frontend format
        return data.map((file) => {
            const item: FileListItem = {
                id: file.id,
                name: file.file_name || "Untitled File",
                updated_at: file.updated_at,
            };
            
            if (file.file_type) item.type = file.file_type;
            if (file.file_size) item.size = file.file_size;
            if (file.messages?.id) item.message_id = file.messages.id;
            if (file.messages?.conversation_id) item.conversation_id = file.messages.conversation_id;
            if (file.messages?.content) item.message_content = file.messages.content;
            if (file.messages?.sender_id) item.sender_id = file.messages.sender_id;
            if (file.messages?.sender) {
                const s = file.messages.sender;
                const name = [s.first_name, s.last_name].filter(Boolean).join(' ');
                if (name) item.sender_name = name;
            }
            
            return item;
        });
    },

    async listFilesPaginated(params?: ListFilesParams): Promise<{ files: FileListItem[]; next_cursor: string | null; has_more: boolean }> {
        const query = new URLSearchParams();
        if (params?.cursor) query.set("cursor", params.cursor);
        if (params?.limit) query.set("limit", String(params.limit));
        if (params?.search) query.set("search", params.search);
        const qs = query.toString();

        const response = await api.get<PaginatedFilesResponse>(`/files${qs ? `?${qs}` : ""}`);
        const { data, next_cursor, has_more } = response.data;
        const files = (Array.isArray(data) ? data : []).map((file) => {
            const item: FileListItem = {
                id: file.id,
                name: file.file_name || "Untitled File",
                updated_at: file.updated_at,
            };
            if (file.file_type) item.type = file.file_type;
            if (file.file_size) item.size = file.file_size;
            if (file.messages?.id) item.message_id = file.messages.id;
            if (file.messages?.conversation_id) item.conversation_id = file.messages.conversation_id;
            if (file.messages?.content) item.message_content = file.messages.content;
            if (file.messages?.sender_id) item.sender_id = file.messages.sender_id;
            if (file.messages?.sender) {
                const s = file.messages.sender;
                const name = [s.first_name, s.last_name].filter(Boolean).join(' ');
                if (name) item.sender_name = name;
            }
            return item;
        });

        return { files, next_cursor, has_more };
    },

    async getFileUrl(fileId: string): Promise<string> {
        const response = await api.get<{ signed_url: string }>(`/files/${fileId}`);
        return response.data.signed_url;
    },

    async downloadFile(fileId: string, fileName?: string): Promise<void> {
        if (isNative()) {
            const blobUrl = await this.getFileBlobUrl(fileId);
            const res = await fetch(blobUrl);
            const blob = await res.blob();
            URL.revokeObjectURL(blobUrl);
            await saveToDevice(blob, fileName || `file-${fileId}`);
            return;
        }
        const signedUrl = await this.getFileUrl(fileId);
        window.open(signedUrl, "_blank");
    },

    /** Fetch file content as a blob URL via the backend proxy (never exposes Supabase URL). */
    async getFileBlobUrl(fileId: string): Promise<string> {
        const response = await api.get(`/files/${fileId}/content`, { responseType: "blob" });
        return URL.createObjectURL(response.data as Blob);
    },

    /** Download a file through the backend proxy, triggering a browser save dialog. */
    async downloadViaProxy(fileId: string, fileName: string): Promise<void> {
        const blobUrl = await this.getFileBlobUrl(fileId);
        if (isNative()) {
            try {
                const res = await fetch(blobUrl);
                const blob = await res.blob();
                URL.revokeObjectURL(blobUrl);
                await saveToDevice(blob, fileName);
                emitToast('success', 'Saved to Files → Documents');
                trackEvent('file_downloaded', { file_id: fileId });
            } catch (err) {
                console.error('Native download failed:', err);
                emitToast('error', 'Download failed. Please try again.');
            }
            return;
        }
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
        trackEvent('file_downloaded', { file_id: fileId });
    },

    /** Open a file in a new tab via its blob URL (no OS download dialog). */
    async openInNewTab(fileId: string, existingBlobUrl?: string): Promise<void> {
        if (isNative()) {
            const { Browser } = await import('@capacitor/browser');
            const signedUrl = await this.getFileUrl(fileId);
            await Browser.open({ url: signedUrl });
            return;
        }
        const blobUrl = existingBlobUrl || await this.getFileBlobUrl(fileId);
        window.open(blobUrl, "_blank", "noopener");
    },

    /** Soft-delete a file. */
    async deleteFile(fileId: string): Promise<void> {
        await api.delete(`/files/${fileId}`);
        trackEvent('file_deleted', { file_id: fileId });
    },

    /**
     * Share a file using the native share sheet (mobile) or copy to clipboard (desktop).
     * Returns 'shared' | 'copied' | 'unsupported' so the caller can show appropriate feedback.
     */
    async shareFile(fileId: string, fileName: string, existingBlobUrl?: string): Promise<"shared" | "copied" | "unsupported"> {
        const blobUrl = existingBlobUrl || await this.getFileBlobUrl(fileId);

        if (isNative()) {
            const { shareFile: platformShare } = await import('../../platform/share');
            return platformShare(fileName, blobUrl);
        }

        const res = await fetch(blobUrl);
        const blob = await res.blob();
        const file = new File([blob], fileName, { type: blob.type || "application/octet-stream" });

        // Native share sheet (iOS / Android)
        if (navigator.canShare?.({ files: [file] })) {
            try {
                await navigator.share({ files: [file] });
                trackEvent('file_shared', { file_id: fileId, result: 'shared' });
                return "shared";
            } catch (err: unknown) {
                // User cancelled the share sheet — not an error
                if (err instanceof Error && err.name === "AbortError") return "shared";
                throw err;
            }
        }

        // Fallback: copy image to clipboard (desktop / unsupported browsers)
        if (navigator.clipboard?.write && blob.type.startsWith("image/")) {
            try {
                // Clipboard API only supports PNG reliably
                let clipBlob = blob;
                if (blob.type !== "image/png") {
                    const bmp = await createImageBitmap(blob);
                    const canvas = new OffscreenCanvas(bmp.width, bmp.height);
                    const ctx = canvas.getContext("2d")!;
                    ctx.drawImage(bmp, 0, 0);
                    clipBlob = await canvas.convertToBlob({ type: "image/png" });
                }
                await navigator.clipboard.write([
                    new ClipboardItem({ "image/png": clipBlob }),
                ]);
                trackEvent('file_shared', { file_id: fileId, result: 'copied' });
                return "copied";
            } catch {
                // Clipboard write failed — fall through
            }
        }

        return "unsupported";
    },
};
